// ==================== VIDEO PLAYER MODAL COMPONENT ====================

import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Volume2, VolumeX, Maximize, Minimize, Loader2 } from 'lucide-react';
import { cn } from '../../utils/helpers';

// ==================== TYPES ====================

interface VideoPlayerProps {
  /** Whether the video player modal is open */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** YouTube video ID or full URL */
  videoId?: string;
  /** Optional title for the video */
  title?: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Auto play when opened */
  autoPlay?: boolean;
  /** Additional class names */
  className?: string;
}

// ==================== EASING CONSTANTS ====================

// Define easing curves as tuples for proper TypeScript typing
const EASE_SMOOTH: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];
const EASE_BOUNCE: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

// ==================== ANIMATION VARIANTS ====================

const backdropVariants = {
  hidden: { 
    opacity: 0,
    backdropFilter: 'blur(0px)'
  },
  visible: { 
    opacity: 1,
    backdropFilter: 'blur(12px)',
    transition: { 
      duration: 0.4,
      ease: EASE_SMOOTH
    }
  },
  exit: { 
    opacity: 0,
    backdropFilter: 'blur(0px)',
    transition: { 
      duration: 0.3,
      ease: EASE_SMOOTH
    }
  }
};

const modalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.85,
    y: 40,
    rotateX: 15
  },
  visible: { 
    opacity: 1, 
    scale: 1, 
    y: 0,
    rotateX: 0,
    transition: { 
      duration: 0.5,
      ease: EASE_BOUNCE,
      opacity: { duration: 0.3 },
      scale: { duration: 0.5 },
      y: { duration: 0.5 }
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.9,
    y: 30,
    transition: { 
      duration: 0.3,
      ease: EASE_SMOOTH
    }
  }
};

const glowVariants = {
  hidden: { 
    opacity: 0,
    scale: 0.8
  },
  visible: { 
    opacity: 1,
    scale: 1,
    transition: { 
      duration: 0.6,
      delay: 0.2,
      ease: EASE_SMOOTH
    }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: { duration: 0.2 }
  }
};

const buttonVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: (delay: number) => ({
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      delay: delay,
      ease: EASE_BOUNCE
    }
  }),
  exit: { 
    opacity: 0, 
    scale: 0.8,
    transition: { duration: 0.2 }
  }
};

const titleVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: 0.3,
      ease: EASE_SMOOTH
    }
  },
  exit: {
    opacity: 0,
    y: 10,
    transition: { duration: 0.2 }
  }
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Extract YouTube video ID from various URL formats
 */
const extractYouTubeId = (input: string): string => {
  if (!input) return '';
  
  // If it's already just an ID (11 characters alphanumeric)
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
    return input;
  }
  
  // Try to extract from various URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
  ];
  
  for (const pattern of patterns) {
    const match = input.match(pattern);
    if (match) return match[1];
  }
  
  return input;
};

// ==================== VIDEO PLAYER COMPONENT ====================

const VideoPlayer = ({
  isOpen,
  onClose,
  videoId = 'V4yP7TG6sr4', // Default demo video
  title,
  subtitle,
  autoPlay = true,
  className
}: VideoPlayerProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const youtubeId = extractYouTubeId(videoId);
  
  // Build YouTube embed URL with parameters
  const embedUrl = `https://www.youtube.com/embed/${youtubeId}?autoplay=${autoPlay ? 1 : 0}&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&mute=${isMuted ? 1 : 0}`;

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle iframe load
  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  // Create a unique key to reset the iframe when videoId changes
  const iframeKey = `${youtubeId}-${isOpen}`;

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 lg:p-8"
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-(--color-midnight-navy)/90"
            variants={backdropVariants}
            onClick={onClose}
          />

          {/* Glow Effects */}
          <motion.div
            className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-(--color-signal-orange)/15 rounded-full blur-[100px] pointer-events-none"
            variants={glowVariants}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-(--color-warm-amber)/10 rounded-full blur-[80px] pointer-events-none"
            variants={glowVariants}
          />

          {/* Close Button - Top Right Corner */}
          <motion.button
            className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50 p-3 rounded-full bg-(--color-charcoal-black)/80 backdrop-blur-sm border border-(--color-border-gray)/50 text-(--color-soft-gray) hover:text-white hover:border-(--color-signal-orange)/50 hover:bg-(--color-charcoal-black) transition-all duration-300 group"
            onClick={onClose}
            variants={buttonVariants}
            custom={0.2}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={20} className="group-hover:rotate-90 transition-transform duration-300" />
          </motion.button>

          {/* Video Container */}
          <motion.div
            ref={containerRef}
            className={cn(
              "relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden shadow-2xl",
              className
            )}
            variants={modalVariants}
          >
            {/* Decorative Border Glow */}
            <div className="absolute -inset-px bg-linear-to-br from-(--color-signal-orange)/30 via-(--color-warm-amber)/20 to-(--color-signal-orange)/30 rounded-2xl blur-sm" />
            <div className="absolute -inset-px bg-linear-to-br from-(--color-signal-orange)/20 via-transparent to-(--color-warm-amber)/20 rounded-2xl" />
            
            {/* Inner Container */}
            <div className="relative bg-(--color-charcoal-black) rounded-2xl overflow-hidden">
              {/* Loading State */}
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-(--color-midnight-navy) z-10">
                  <div className="flex flex-col items-center gap-4">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                    >
                      <Loader2 size={40} className="text-(--color-signal-orange)" />
                    </motion.div>
                    <span className="text-(--color-muted-gray) text-sm">Loading video...</span>
                  </div>
                </div>
              )}

              {/* YouTube Embed */}
              <iframe
                key={iframeKey}
                ref={iframeRef}
                src={embedUrl}
                className="w-full h-full aspect-video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                onLoad={handleIframeLoad}
                title={title || "Video Player"}
              />

              {/* Custom Controls Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-linear-to-t from-(--color-midnight-navy)/90 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300">
                <div className="flex items-center justify-between gap-4">
                  {/* Left Controls */}
                  <div className="flex items-center gap-2">
                    <motion.button
                      className="p-2 rounded-lg bg-(--color-charcoal-black)/60 backdrop-blur-sm border border-(--color-border-gray)/30 text-(--color-soft-gray) hover:text-white hover:border-(--color-signal-orange)/50 transition-all duration-200"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setIsMuted(!isMuted)}
                    >
                      {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                    </motion.button>
                  </div>

                  {/* Right Controls */}
                  <div className="flex items-center gap-2">
                    <motion.button
                      className="p-2 rounded-lg bg-(--color-charcoal-black)/60 backdrop-blur-sm border border-(--color-border-gray)/30 text-(--color-soft-gray) hover:text-white hover:border-(--color-signal-orange)/50 transition-all duration-200"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={toggleFullscreen}
                    >
                      {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Video Info - Below Video */}
          {(title || subtitle) && (
            <motion.div
              className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 text-center max-w-2xl px-4"
              variants={titleVariants}
            >
              {title && (
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-1 font-heading">
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className="text-sm text-(--color-muted-gray)">
                  {subtitle}
                </p>
              )}
            </motion.div>
          )}

          {/* Keyboard Hint */}
          <motion.div
            className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 hidden sm:flex items-center gap-2 text-xs text-(--color-muted-gray)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <kbd className="px-2 py-1 rounded bg-(--color-charcoal-black)/60 border border-(--color-border-gray)/30 font-mono">
              ESC
            </kbd>
            <span>to close</span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ==================== VIDEO TRIGGER BUTTON COMPONENT ====================

interface VideoTriggerButtonProps {
  /** Callback when button is clicked */
  onClick: () => void;
  /** Button text */
  children?: React.ReactNode;
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Additional class names */
  className?: string;
}

const playIconVariants = {
  initial: { scale: 1, x: 0 },
  hover: { 
    scale: 1.2, 
    x: 2,
    transition: { duration: 0.3, ease: EASE_BOUNCE }
  },
  tap: { scale: 0.9 }
};

const ringVariants = {
  initial: { scale: 1, opacity: 0.5 },
  hover: {
    scale: 1.4,
    opacity: 0,
    transition: { duration: 0.6, repeat: Infinity }
  }
};

const VideoTriggerButton = ({
  onClick,
  children = 'Watch Demo',
  size = 'lg',
  variant = 'secondary',
  className
}: VideoTriggerButtonProps) => {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm gap-2',
    md: 'px-5 py-2.5 text-sm gap-2',
    lg: 'px-6 py-3 text-base gap-3'
  };

  const variantClasses = {
    primary: 'bg-linear-to-r from-(--color-signal-orange) to-(--color-warm-amber) text-white shadow-orange hover:shadow-orange-lg',
    secondary: 'bg-(--color-charcoal-black)/80 backdrop-blur-sm border border-(--color-border-gray) text-white hover:border-(--color-signal-orange)/50 hover:bg-(--color-charcoal-black)',
    ghost: 'bg-transparent text-(--color-soft-gray) hover:text-white hover:bg-(--color-charcoal-black)/30'
  };

  const iconSizes = {
    sm: 14,
    md: 16,
    lg: 18
  };

  return (
    <motion.button
      className={cn(
        "relative inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-300 group overflow-hidden",
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      onClick={onClick}
      whileHover="hover"
      whileTap="tap"
      initial="initial"
    >
      {/* Animated Background Shimmer */}
      <motion.div
        className="absolute inset-0 bg-linear-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100"
        initial={{ x: '-100%' }}
        whileHover={{ x: '100%' }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
      />

      {/* Play Icon with Animation */}
      <span className="relative flex items-center justify-center">
        <motion.span
          className="absolute inset-0 rounded-full bg-(--color-signal-orange)/20"
          variants={ringVariants}
        />
        <motion.span variants={playIconVariants}>
          <Play 
            size={iconSizes[size]} 
            className="text-(--color-signal-orange) fill-(--color-signal-orange)/30 group-hover:fill-(--color-signal-orange)/50 transition-all duration-300" 
          />
        </motion.span>
      </span>

      {/* Button Text */}
      <span className="relative">{children}</span>
    </motion.button>
  );
};

// ==================== EXPORTS ====================

export { VideoPlayer, VideoTriggerButton };
export type { VideoPlayerProps, VideoTriggerButtonProps };

